import { inject } from '@angular/core';
import { Route } from '@angular/router';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { initialDataResolver } from 'app/app.resolvers';
import { StorageKeys, StoragePort } from 'app/core/application/ports/storage.port';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { getFirstMenuLink, MenuAccessGuard } from 'app/core/navigation/guards/menu-access.guard';
import { LayoutComponent } from 'app/layout/layout.component';

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */

const resolveInitialRedirect = (): string => {
    const menu = inject(StoragePort).getItem<FuseNavigationItem[]>(StorageKeys.USER_MENU);
    return getFirstMenuLink(menu) ?? '/sign-in';
};

export const appRoutes: Route[] = [

    // Redirect empty path según el rol
    {path: '', pathMatch: 'full', redirectTo: () => resolveInitialRedirect()},

    // Redirect signed-in user to the '/example'
    //
    // After the user signs in, the sign-in page will redirect the user to the 'signed-in-redirect'
    // path. Below is another redirection for that path to redirect the user to the desired
    // location. This is a small convenience to keep all main routes together here on this file.
    {
        path: 'signed-in-redirect',
        pathMatch: 'full',
        redirectTo: () => resolveInitialRedirect(),
    },

    // Auth routes for guests
    {
        path: '',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            {path: 'confirmation-required', loadChildren: () => import('app/modules/auth/confirmation-required/confirmation-required.routes')},
            {path: 'forgot-password', loadChildren: () => import('app/modules/auth/forgot-password/forgot-password.routes')},
            {path: 'reset-password', loadChildren: () => import('app/modules/auth/reset-password/reset-password.routes')},
            {path: 'sign-in', loadChildren: () => import('app/modules/auth/sign-in/sign-in.routes')},
            {path: 'sign-up', loadChildren: () => import('app/modules/auth/sign-up/sign-up.routes')}
        ]
    },

    // Auth routes for authenticated users
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            {path: 'sign-out', loadChildren: () => import('app/modules/auth/sign-out/sign-out.routes')},
            {path: 'unlock-session', loadChildren: () => import('app/modules/auth/unlock-session/unlock-session.routes')}
        ]
    },

    // Landing routes
    {
        path: '',
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            {path: 'home', loadChildren: () => import('app/modules/landing/home/home.routes')},
        ]
    },

    // Admin routes
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        // data: {
        //     layout: 'classy'
        // },
        resolve: {
            initialData: initialDataResolver
        },
        children: [
            {path: 'example', canMatch: [MenuAccessGuard], loadChildren: () => import('app/modules/admin/example/example.routes')},
            {path: 'gestion-admin', canMatch: [MenuAccessGuard], loadChildren: () => import('app/modules/gestion-admin/gestion-admin.routes')},
            {path: 'gestion-cliente', canMatch: [MenuAccessGuard], loadChildren: () => import('app/modules/gestion-cliente/gestion-cliente.routes')},
            {path: 'gestion-chat', canMatch: [MenuAccessGuard], loadChildren: () => import('app/modules/gestion-chat/chat.routes')},
            {path: 'gestion-configuracion', canMatch: [MenuAccessGuard], loadChildren: () => import('app/modules/gestion-configuracion/gestion-configuracion.routes')},
            {path: 'gestion-inicio', canMatch: [MenuAccessGuard], loadChildren: () => import('app/modules/gestion-inicio/gestion-inicio.routes')},
            {path: 'gestion-super-admin', canMatch: [MenuAccessGuard], loadChildren: () => import('app/modules/gestion-super-admin/gestion-super-admin.routes')},
            {path: 'gestion-movimiento', canMatch: [MenuAccessGuard], loadChildren: () => import('app/modules/gestion-movimiento/gestion-movimiento.routes')},
        ]
    }
];
